import { normalizeUrlPath } from "@/axios/errors";
import * as Sentry from "@sentry/react";
import { ZodError, type SafeParseReturnType, type z } from "zod";

export type ZodSentrySource = "api";

export type ZodSentryContext = {
	source: ZodSentrySource;
	tags?: Record<string, string>;
};

export type FlattenedZodIssue = {
	path: string;
	code: string;
	message: string;
};

type AxiosParseConfig = {
	method?: string;
	url?: string;
};

type AxiosParseResponse = {
	data: unknown;
	config: AxiosParseConfig;
};

export class ZodSentryError extends Error {
	constructor(message: string) {
		super(message);
		this.name = "ZodSentryError";
	}
}

function formatIssuePath(path: PropertyKey[]): string {
	if (path.length === 0) {
		return "(root)";
	}

	return path.reduce<string>((formatted, segment, index) => {
		if (typeof segment === "number") {
			return `${formatted}[${segment}]`;
		}

		const key = String(segment);
		return index === 0 ? key : `${formatted}.${key}`;
	}, "");
}

function flattenZodIssues(error: ZodError): FlattenedZodIssue[] {
	return error.issues.map((issue) => ({
		path: formatIssuePath(issue.path),
		code: issue.code,
		message: issue.message,
	}));
}

function uniqueIssuePaths(issues: FlattenedZodIssue[]): string[] {
	return [...new Set(issues.map((issue) => issue.path))];
}

const FINGERPRINT_TAG_KEYS = ["http_method", "endpoint", "form"] as const;

export function zodSentryFingerprint(
	context: ZodSentryContext,
	issues: FlattenedZodIssue[],
): string[] {
	const firstPath = issues[0]?.path ?? "unknown-path";
	const tagParts = FINGERPRINT_TAG_KEYS.flatMap((key) => {
		const value = context.tags?.[key];
		return value ? [value] : [];
	});
	return ["zod-error", context.source, ...tagParts, firstPath];
}

export function fromAxiosResponse(response: {
	config: AxiosParseConfig;
}): ZodSentryContext {
	return {
		source: "api",
		tags: {
			http_method: (response.config.method ?? "?").toUpperCase(),
			endpoint: normalizeUrlPath(response.config.url),
		},
	};
}

function formatZodSentryMessage(
	context: ZodSentryContext,
	issues: FlattenedZodIssue[],
): string {
	const properties = uniqueIssuePaths(issues).join(", ") || "unknown issue";
	const location = [context.tags?.http_method, context.tags?.endpoint]
		.filter(Boolean)
		.join(" ");
	const where = location ? ` ${location}` : "";
	return `${context.source}${where} \u2014 ${properties}`;
}

// biome-ignore lint/complexity/noStaticOnlyClass: namespaced parse + Sentry reporting API
export class ZodSentryReporter {
	static parse<TSchema extends z.ZodTypeAny>(
		schema: TSchema,
		response: AxiosParseResponse,
	): z.infer<TSchema> {
		const result = schema.safeParse(response.data);
		if (!result.success) {
			ZodSentryReporter.capture(result.error, fromAxiosResponse(response));
			throw result.error;
		}
		return result.data;
	}

	static safeParse<TSchema extends z.ZodTypeAny>(
		schema: TSchema,
		response: AxiosParseResponse,
	): SafeParseReturnType<z.input<TSchema>, z.infer<TSchema>> {
		const result = schema.safeParse(response.data);
		if (!result.success) {
			ZodSentryReporter.capture(result.error, fromAxiosResponse(response));
		}
		return result;
	}

	private static capture(error: ZodError, context: ZodSentryContext): void {
		const issues = flattenZodIssues(error);
		const paths = uniqueIssuePaths(issues);
		Sentry.captureException(
			new ZodSentryError(formatZodSentryMessage(context, issues)),
			{
				tags: {
					...context.tags,
					zod_source: context.source,
					zod_issue_code: error.issues[0]?.code ?? "unknown",
					zod_issue_path: paths[0] ?? "unknown-path",
				},
				fingerprint: zodSentryFingerprint(context, issues),
				extra: { paths, issues },
			},
		);
	}
}
